import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, AlertCircle, User, Lock, Mail, Building2, Check, Eye, EyeOff } from 'lucide-react';
import { loginUser, createUser } from '../lib/auth';
import { User as UserType } from '../lib/types';
import LinkedInLoginButton from './LinkedInLoginButton';
import { sendWelcomeEmail, sendNewUserNotification } from '../lib/email';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (user: UserType) => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Form submitted");
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      if (isSignUp) {
        if (!firstName || !lastName || !email || !password || !company) {
          throw new Error('Veuillez remplir tous les champs obligatoires');
        }

        // Create new user
        const newUser = await createUser(
          email.trim(),
          password,
          `${firstName} ${lastName}`
        );

        console.log("New user created:", newUser);

        // Generate login link
        const loginLink = `${window.location.origin}/login?email=${encodeURIComponent(email)}`;

        // Send welcome email
        await sendWelcomeEmail(newUser, loginLink);

        // Send notification to admin
        await sendNewUserNotification({
          firstName,
          lastName,
          email,
          company
        });

        setSuccess('Compte créé avec succès !');
        console.log("Updating app state with new user...");
        onLoginSuccess(newUser);
        
        // Navigate immediately without delay
        console.log("Redirecting to dashboard...");
        onClose();
        navigate('/dashboard');
        console.log("Navigation completed");

      } else {
        console.log("Attempting login with:", email);
        const user = await loginUser(email.trim(), password);
        console.log("Login successful:", user);
        
        setSuccess('Connexion réussie !');
        console.log("Updating app state with logged in user...");
        
        // Update app state first
        onLoginSuccess(user);
        
        // Then close modal and navigate
        console.log("Closing modal and redirecting...");
        onClose();
        navigate('/dashboard');
        console.log("Navigation completed");
      }
    } catch (error: any) {
      console.error("Authentication error:", error);
      setError(error.message || 'Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkedInLoginSuccess = (user: UserType) => {
    console.log("LinkedIn login successful, updating state...");
    onLoginSuccess(user);
    console.log("Closing modal and redirecting...");
    onClose();
    navigate('/dashboard');
    console.log("Navigation completed");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold gradient-text">
            {isSignUp ? 'Créer un compte' : 'Connexion'}
          </h3>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4 flex items-center text-sm">
            <AlertCircle className="w-4 h-4 mr-2" />
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 text-green-600 p-3 rounded-md mb-4 flex items-center text-sm">
            <Check className="w-4 h-4 mr-2" />
            {success}
          </div>
        )}
        
        <div className="space-y-4">
          <LinkedInLoginButton 
            className="w-full" 
            onSuccess={handleLinkedInLoginSuccess}
            onError={(error) => setError(error)}
          />
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">ou</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignUp && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prénom *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                        required
                        placeholder="Jean"
                      />
                      <User className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                        required
                        placeholder="Dupont"
                      />
                      <User className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Société *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                      placeholder="Nom de votre entreprise"
                    />
                    <Building2 className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(null);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                  placeholder="email@example.com"
                />
                <Mail className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mot de passe *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError(null);
                  }}
                  className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                  placeholder="••••••••"
                  minLength={8}
                />
                <Lock className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {isSignUp && (
                <p className="mt-1 text-xs text-gray-500">
                  Le mot de passe doit contenir au moins 8 caractères
                </p>
              )}
            </div>
            
            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <span>{isSignUp ? 'Créer mon compte' : 'Se connecter'}</span>
                )}
              </button>
            </div>

            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setError(null);
                  setSuccess(null);
                  setPassword('');
                  setEmail('');
                }}
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                {isSignUp ? 'Déjà un compte ? Se connecter' : 'Créer un compte'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;